import { ThumbnailSize, ThumbnailType, Video } from "@/api";
import { APP_ID } from "@/env.ts";

export const VIDAPP_FILES_DOMAIN = "https://files.vidapp.com";
export const DEFAULT_THUMBNAIL = `${VIDAPP_FILES_DOMAIN}/images/builder/misc/default_thumbnail.png`;
export const S3_BUCKET = "https://storage.vidapp.com";
export const prefix = `${S3_BUCKET}/${APP_ID}/images`;
export const TAB_ICON_URL = `${S3_BUCKET}/images/icons/`;

export const getUrlFromFilename = (url: string) => {
  if (url.toLowerCase().startsWith("http")) {
    return url;
  }
  return `${prefix}/${url}`;
};

export type ThumbnailUrlProps = Partial<
  Pick<
    Video,
    | "Thumbnails"
    | "ThumbnailSource"
    | "SourceThumbnailSource"
    | "SquareThumbnails"
    | "PortraitThumbnails"
    | "SquareThumbnailSource"
    | "PortraitThumbnailSource"
    | "SourcePortraitThumbnailSource"
    | "SourceSquareThumbnailSource"
    | "Properties"
  >
>;
export const getContentThumbnailUrl = (
  content: ThumbnailUrlProps,
  size?: ThumbnailSize,
  type?: ThumbnailType,
) => {
  if (content) {
    if (!type || type === "landscape") {
      if (content.Thumbnails) {
        if (size) {
          const sizedThumbnail = content.Thumbnails[size];
          if (sizedThumbnail) {
            return getUrlFromFilename(sizedThumbnail);
          }
        }
        const mediumThumbnail = content.Thumbnails["medium"];
        if (mediumThumbnail) {
          return mediumThumbnail;
        }
      }
      if (content.ThumbnailSource) {
        return getUrlFromFilename(content.ThumbnailSource);
      }
      if (content.SourceThumbnailSource) {
        return getUrlFromFilename(content.SourceThumbnailSource);
      }
    } else if (type === "square") {
      if (content.SquareThumbnails) {
        if (size) {
          const sizedThumbnail = content.SquareThumbnails[size];
          if (sizedThumbnail) {
            return getUrlFromFilename(sizedThumbnail);
          }
        }
        const mediumThumbnail = content.SquareThumbnails["medium"];
        if (mediumThumbnail) {
          return mediumThumbnail;
        }
      }
      if (content.SquareThumbnailSource) {
        return getUrlFromFilename(content.SquareThumbnailSource);
      }
      if (content.SourceSquareThumbnailSource) {
        return getUrlFromFilename(content.SourceSquareThumbnailSource);
      }
    } else if (type === "portrait") {
      if (content.PortraitThumbnails) {
        if (size) {
          const sizedThumbnail = content.PortraitThumbnails[size];
          if (sizedThumbnail) {
            return getUrlFromFilename(sizedThumbnail);
          }
        }
        const mediumThumbnail = content.PortraitThumbnails["medium"];
        if (mediumThumbnail) {
          return mediumThumbnail;
        }
      }
      if (content.PortraitThumbnailSource) {
        return getUrlFromFilename(content.PortraitThumbnailSource);
      }
      if (content.SourcePortraitThumbnailSource) {
        return getUrlFromFilename(content.SourcePortraitThumbnailSource);
      }
    } else if (type === "wideLandscape") {
      const sizedThumbnail = content.Properties?.WideLandscapeThumbnail;
      if (sizedThumbnail) {
        return getUrlFromFilename(sizedThumbnail);
      }
    }
  }

  return DEFAULT_THUMBNAIL;
};

export const getTabIconFilename = ({ icon }: { icon?: string }) => {
  if (icon?.startsWith("http")) {
    return icon;
  }
  if (icon) {
    return `${TAB_ICON_URL}${icon}`;
  }
  return undefined;
};

// Resize an image with maxWidth and maxHeight while maintaining aspect ratio
export const resizeImage = (
  file: File,
  maxWidth: number,
  maxHeight: number,
): Promise<{ resizedFile: File; base64: string }> => {
  return new Promise((resolve) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      let { width, height } = img;

      if (width > maxWidth || height > maxHeight) {
        const aspectRatio = width / height;

        if (width > height) {
          width = Math.min(width, maxWidth);
          height = width / aspectRatio;
        } else {
          height = Math.min(height, maxHeight);
          width = height * aspectRatio;
        }
      }

      canvas.width = width;
      canvas.height = height;
      ctx?.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            const resizedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now(),
            });

            const reader = new FileReader();
            reader.onload = () => {
              resolve({
                resizedFile,
                base64: reader.result as string,
              });
            };
            reader.readAsDataURL(resizedFile);
          }
        },
        file.type,
        1.0,
      );
    };

    img.src = URL.createObjectURL(file);
  });
};
